import { useEffect, useRef, useState } from 'react';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { archiveApi } from '../api/client';
import { usePageImage } from '../api/usePageImage';
import type { PageHighlights } from '../api/types';
import { PageCanvas } from './PageCanvas';

/**
 * The whole scanned page on the result card, with the matches marked on it.
 *
 * The whole page, not a crop of the hit. A crop reads the word for you but throws
 * away what the page IS — a letter, a table, a form, a signature sheet — and
 * where on it the term sits, which is most of what tells a reader whether this is
 * the document they want. The marks stay visible as orange blocks even at
 * thumbnail size, so position survives the scale.
 *
 * Geometry is fetched only once the card is near the viewport. A result screen
 * holds up to topK pages; loading every one of them would mean pulling the word
 * geometry of three hundred pages to draw the handful someone can actually see.
 */

interface HitPreviewProps {
  pdfId: number;
  page: number;
  query: string;
  /** Rendered height in pixels; A4 proportions decide the width. */
  height?: number;
}

const rootMargin = '300px';

export function HitPreview({ pdfId, page, query, height = 168 }: HitPreviewProps) {
  const [marks, setMarks] = useState<PageHighlights | null>(null);
  const [near, setNear] = useState(() => typeof IntersectionObserver === 'undefined');
  const [failed, setFailed] = useState(false);
  const anchor = useRef<HTMLDivElement>(null);
  // Only once the card is near the viewport: a result screen holds up to topK
  // of these, and fetching every render would pull megabytes nobody looks at.
  const imageUrl = usePageImage(near ? pdfId : undefined, near ? page : undefined);

  useEffect(() => {
    if (near) return;
    const element = anchor.current;
    if (!element) return;

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        setNear(true);
        observer.disconnect();
      }
    }, { rootMargin });

    observer.observe(element);
    return () => observer.disconnect();
  }, [near]);

  useEffect(() => {
    if (!near) return;
    const controller = new AbortController();

    archiveApi.highlights(pdfId, page, query, controller.signal)
      .then(setMarks)
      .catch(() => { if (!controller.signal.aborted) setFailed(true); });

    return () => controller.abort();
  }, [near, pdfId, page, query]);

  // A4 portrait. Taken from the render the API reports rather than assumed, so a
  // landscape or rotated scan is not squeezed into a portrait box.
  const ratio = marks && marks.render.height > 0 ? marks.render.width / marks.render.height : 0.707;
  const frame = { width: Math.round(height * ratio), height, flexShrink: 0 };

  if (failed || (marks && !marks.has_geometry)) {
    // Said, not left blank. Most of the corpus has no coordinates yet, and an
    // empty grey box next to a hit reads as a broken image rather than as a page
    // that has not been analysed.
    return (
      <Stack
        ref={anchor}
        sx={{ ...frame, borderRadius: 1, border: '1px dashed', borderColor: 'divider', color: 'text.disabled' }}
        alignItems="center"
        justifyContent="center"
        spacing={0.25}
      >
        <ImageNotSupportedOutlinedIcon fontSize="small" />
        <Typography variant="caption" sx={{ fontSize: 10, textAlign: 'center', px: 0.5 }}>
          keine Koordinaten
        </Typography>
      </Stack>
    );
  }

  if (!marks) {
    return (
      <Box ref={anchor} sx={frame}>
        <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 1 }} />
      </Box>
    );
  }

  return (
    <Box
      ref={anchor}
      sx={{
        ...frame,
        borderRadius: 1,
        overflow: 'hidden',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#fff',
        display: 'grid',
        placeItems: 'center'
      }}
    >
      <PageCanvas
        imageUrl={imageUrl}
        width={marks.render.width}
        height={marks.render.height}
        highlights={marks.highlights}
        alt={`Seite ${page} mit ${marks.highlights.length} Treffer(n)`}
      />
    </Box>
  );
}
